import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Grid, Heading, Tabs, Layout } from '@design-systems/canvas-ui'
import { WORKSPACE_TYPES } from '../utils/workspaceConsts'
import _ from 'lodash'
import { FavoriteButton } from '@plm-core/favorites'
import SparkStyledBreadcrumbs from 'components/legacy/SparkStyledBreadcrumbs'

type WorkspaceData = {
  workspaceId: string
  workspaceType: number
  workspaceDescription: string
}

type TabConfig = {
  name: string
  isActive: (pathname: string, workspace?: WorkspaceData) => boolean
  to: (workspace: WorkspaceData) => string
  breadcrumb?: string
  supportedWorkspaceTypes: number[]
}

const tabConfig: TabConfig[] = [
  {
    name: 'Products',
    to: (workspace) => `/workspaces/${workspace.workspaceId}/products`,
    isActive: (pathname: string) => pathname.includes('/products'),
    supportedWorkspaceTypes: [
      WORKSPACE_TYPES.product,
      WORKSPACE_TYPES.research,
    ],
  },
  {
    name: 'Collab Canvas',
    to: (workspace) => `/workspaces/${workspace.workspaceId}/boards`,
    isActive: (pathname: string) => pathname.includes('/boards'),
    supportedWorkspaceTypes: [
      WORKSPACE_TYPES.product,
      WORKSPACE_TYPES.research,
    ],
  },
  {
    name: 'Combinations',
    to: (workspace) => `/workspaces/${workspace.workspaceId}/combinations`,
    isActive: (pathname: string) => pathname.includes('/combinations'),
    supportedWorkspaceTypes: [WORKSPACE_TYPES.combination],
  },
  {
    name: 'Discussions',
    to: (workspace) => `/workspaces/${workspace.workspaceId}/discussions`,
    isActive: (pathname: string) => pathname.includes('/discussions'),
    supportedWorkspaceTypes: [
      WORKSPACE_TYPES.product,
      WORKSPACE_TYPES.combination,
      WORKSPACE_TYPES.research,
    ],
  },
  {
    name: 'Files',
    to: (workspace) => `/workspaces/${workspace.workspaceId}/files`,
    isActive: (pathname: string) => pathname.includes('/files'),
    supportedWorkspaceTypes: [
      WORKSPACE_TYPES.product,
      WORKSPACE_TYPES.combination,
      WORKSPACE_TYPES.research,
    ],
  },
  {
    name: 'Teams',
    to: (workspace) => `/workspaces/${workspace.workspaceId}/teams`,
    isActive: (pathname: string) => pathname.includes('/teams'),
    supportedWorkspaceTypes: [
      WORKSPACE_TYPES.product,
      WORKSPACE_TYPES.combination,
      WORKSPACE_TYPES.research,
    ],
  },
  {
    name: 'Product Plan',
    to: (workspace) => `/workspaces/${workspace.workspaceId}`,
    isActive: (pathname: string, workspace) =>
      workspace?.workspaceType === WORKSPACE_TYPES.product &&
      pathname === `/workspaces/${workspace?.workspaceId}`,
    supportedWorkspaceTypes: [WORKSPACE_TYPES.product],
  },
  {
    name: 'Workspace Info',
    to: (workspace) => `/workspaces/${workspace.workspaceId}`,
    isActive: (pathname: string, workspace) =>
      workspace?.workspaceType !== WORKSPACE_TYPES.product &&
      pathname === `/workspaces/${workspace?.workspaceId}`,
    supportedWorkspaceTypes: [
      WORKSPACE_TYPES.combination,
      WORKSPACE_TYPES.research,
    ],
  },
]

export type WorkspaceViewTabsProps = {
  workspaceId: string
  workspaceType: number
  workspaceDescription: string
  includeSparkPadding?: boolean
  breadcrumbs?: { content: string | React.ReactNode; to: string }[]
}

export const WorkspaceViewTabs = ({
  workspaceId,
  workspaceType,
  workspaceDescription,
  includeSparkPadding,
  breadcrumbs,
}: WorkspaceViewTabsProps) => {
  const location = useLocation()
  const navigate = useNavigate()

  const tabs = tabConfig.filter(
    (tab) =>
      workspaceType && tab.supportedWorkspaceTypes.includes(workspaceType),
  )
  const activeTab = _.find(tabs, (tab) =>
    tab.isActive(location.pathname, {
      workspaceId,
      workspaceType,
      workspaceDescription,
    }),
  )
  const handleTabClick = (tab: TabConfig) => () => {
    navigate(tab.to({ workspaceId, workspaceType, workspaceDescription }))
  }

  return (
    <>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Layout.PageHeader>
          <div className="hc8-ph-2x">
            <SparkStyledBreadcrumbs breadcrumbs={breadcrumbs} />
          </div>
        </Layout.PageHeader>
      )}
      <Grid.Container
        className={`${includeSparkPadding ? 'hc8-ph-4x hc8-pt-3x hc8-pb-1x' : 'hc8-pv-1x '}`}
      >
        <Grid.Item
          size={{
            xs: 12,
            md: 9,
          }}
        >
          <Tabs activeTab={activeTab?.name} spacing="expanded">
            {tabs.map((tab) => (
              <Tabs.Item
                key={tab.name}
                name={tab.name}
                onClick={handleTabClick(tab)}
              >
                {tab.name}
              </Tabs.Item>
            ))}
          </Tabs>
        </Grid.Item>
        <Grid.Item
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <Grid.Container justify="end" align="center" spacing="dense">
            <Grid.Item
              data-test="currentWorkspace-favorite"
              size={{
                xs: 1,
              }}
            >
              <FavoriteButton entityId={workspaceId} entityType="workspace" />
            </Grid.Item>
            <Grid.Item
              data-test="currentWorkspace-header"
              size={{
                xs: 11,
              }}
            >
              <Heading
                size={4}
                className="hc8-ws-no-wrap hc8-to-ellipsis hc8-ov-hidden"
              >
                {workspaceDescription}
              </Heading>
            </Grid.Item>
          </Grid.Container>
        </Grid.Item>
      </Grid.Container>
    </>
  )
}

export default WorkspaceViewTabs
